/**
 * What is waiting on a reviewer, counted where they will see it.
 *
 * The approval gate worked and nobody used it, for a mundane reason: nothing told the reviewer
 * anything had arrived. A skill saved out of an SME conversation sat at `in_review` until a
 * Creator happened to open the Skills hub and notice a number. That is not a review process; it
 * is a review process that depends on someone remembering.
 *
 * The counts are polled rather than pushed. A push channel is the better answer eventually, but
 * a poll that works today beats a socket that is not built, and review latency is measured in
 * hours, not seconds.
 */

import { useQuery } from '@tanstack/react-query';
import { useRole } from './useRole';
import { barnskillsApi, type Barnskill } from '../services/barnskillsApi';
import { skillsAwaitingReview } from '../services/pendingReviews';
import { getKbHealth } from '../services/kbApi';

const POLL_MS = 2 * 60 * 1000;

export interface PendingReviews {
  skills: number;
  articles: number;
  total: number;
  /** The waiting skills themselves — the alert links to them by name rather than to a count. */
  pendingSkills: Barnskill[];
}

export function usePendingReviews(): PendingReviews & { isLoading: boolean } {
  const { hasRole } = useRole();
  const enabled = hasRole('creator');

  const skillsQuery = useQuery({
    queryKey: ['pending-reviews', 'skills'],
    queryFn: () => barnskillsApi.list(),
    enabled,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });

  const kbQuery = useQuery({
    queryKey: ['pending-reviews', 'kb'],
    queryFn: () => getKbHealth(),
    enabled,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });

  const pendingSkills = skillsQuery.data ? skillsAwaitingReview(skillsQuery.data) : [];
  const skills = pendingSkills.length;
  const articles = kbQuery.data?.draft ?? 0;

  return {
    skills,
    articles,
    pendingSkills,
    total: skills + articles,
    isLoading: skillsQuery.isLoading || kbQuery.isLoading,
  };
}
